import { Button, Col, NewDateInput, AccessibleForm, Heading, Loader, Row, SelectInput, StepperContext, TextInput, Alert, } from '@digitools/honest-react';
import React, { useContext, useRef, useState } from 'react';
import LCIApi from '../../../../api/LCIApi';
import useAuthentication from '../../../../hooks/useAuthentication';
import useBdmLciConfig from '../../../../hooks/useBdmLciConfig';
import useExperience from '../../../../hooks/useExperience';
import { formatLowerCase, formatName, formatPhone, formatTitleCase, lettersOnly, validateSSNFormat, validateAlphaNumeric, validateName, validatePhoneNotRequired, numbersOnly, } from 'src/utils/validationUtils';
import Address from '../../components/address/Address';
import { LCI_EMPLOYEE_MARITAL_STATUS, LCI_GENDER, MAX_LENGTH_LNAME, MAX_LENGTH_FNAME, } from '../../constants/LCIConstant';
import { getLabels } from '../../utils/LCIUtils';
import CancelLci from '../cancelLCI/CancelLci';
import useTealium from '../../../../hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { BorderLessGrayBox, StyledDiv } from 'src/components/Formatting/StyledComponents';
import { validateDateIsOnOrBeforeToday } from 'src/packages/cli/utils/validationUtils';
import useTranslate from 'src/hooks/useTranslate';
import { parse } from 'date-fns';
import { LciValidationLabels } from '../../constants/LciValidationLabels';
const EMP_ID = 'EMP ID';
const SSN = 'SSN';
const BOTH = 'BOTH';
const SEARCH_CRITERIA_DEFAULT_VALUE = 'EMP ID';
const EmployeeInfo = ({ claimant, event, onComplete }) => {
    const { authHeader } = useAuthentication();
    const stepper = useContext(StepperContext);
    const { customContents, lciApplication } = useBdmLciConfig();
    const { language } = useExperience();
    const { trackEvent } = useTealium();
    const { t } = useTranslate();
    const [showSpinner, setShowSpinner] = useState(false);
    const [isPrefillError, setIsPrefillError] = useState(false);
    const [prefillMessage, setPrefillMessage] = useState('');
    const getCustomLabels = getLabels(customContents, language);
    const labelEmployeeID = getCustomLabels.employeeId || 'Employee ID';
    const labelEmployee = getCustomLabels.employee || 'Employee';
    const eeFormApiRef = useRef();
    const prefillFormApiRef = useRef();
    const setFormApi = (formApi) => {
        eeFormApiRef.current = formApi;
    };
    const setPrefillFormApi = (formApi) => {
        prefillFormApiRef.current = formApi;
    };
    const handleContinue = (values) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.SELF_SERVICE,
            event_name: 'continue',
            event_type: TEALIUM_EVENT.EVENT_TYPE.LIFE_CLAIM_INTAKE,
            event_version: `${labelEmployee} info`,
            event_status: 'step 3'
        });
        const info = {
            ssn: prefillFormApiRef?.current?.getValue('ssnPrefill'),
            employeeId: prefillFormApiRef?.current?.getValue('employeeIdPrefill'),
            firstName: values.employeeInfo_firstName,
            lastName: values.employeeInfo_lastName,
            middleInitial: values.employeeInfo_middleInitial,
            gender: values.employeeInfo_gender,
            dob: values.employeeInfo_dob,
            maritalStatus: values.employeeInfo_maritalStatus,
            phoneNumber: values.employeeInfo_phoneNumber,
            streetAddress1: values.employeeInfo_street1,
            streetAddress2: values.employeeInfo_street2,
            city: values.employeeInfo_city,
            state: values.employeeInfo_state,
            province: values.employeeInfo_province,
            postalcode: values.employeeInfo_postalcode,
            country: values.employeeInfo_country,
        };
        onComplete(info);
        stepper.next();
        window.scrollTo(0, 0);
    };
    const handleBack = () => {
        stepper.back();
        window.scrollTo(0, 0);
    };
    const handleSubmit = () => {
        if (eeFormApiRef && eeFormApiRef.current && prefillFormApiRef && prefillFormApiRef.current) {
            prefillFormApiRef.current.validate();
            eeFormApiRef.current.validate();
            const prefillFormInvalid = prefillFormApiRef.current.getState().invalid;
            const eeFormInvalid = eeFormApiRef.current.getState().invalid;
            if (!prefillFormInvalid && !eeFormInvalid) {
                eeFormApiRef.current.submitForm();
            }
        }
        else if (eeFormApiRef && eeFormApiRef.current) {
            eeFormApiRef.current.validate();
            if (!eeFormApiRef.current.getState().invalid) {
                eeFormApiRef.current.submitForm();
            }
        }
    };
    const handlePrefill = (values) => {
        setShowSpinner(true);
        clearPrefillAlert();
        const prefillRequestData = {
            employeeId: values.employeeIdPrefill,
            ssn: values.ssnPrefill,
            lastName: values.lastNamePrefill.trim(),
            dob: values.dobPrefill
        };
        LCIApi()
            .prefill(prefillRequestData, authHeader())
            .then(({ data, status }) => {
            if (status === 200) {
                if (eeFormApiRef && eeFormApiRef.current) {
                    eeFormApiRef.current.setValue('employeeInfo_firstName', formatTitleCase(data.firstName));
                    eeFormApiRef.current.setValue('employeeInfo_lastName', formatTitleCase(data.lastName));
                    eeFormApiRef.current.setValue('employeeInfo_middleInitial', formatTitleCase(data.middleInitial));
                    if (data.gender && !!data.gender.trim()) {
                        eeFormApiRef.current.setValue('employeeInfo_gender', data.gender);
                    }
                    eeFormApiRef.current.setValue('employeeInfo_phoneNumber', data.phoneNumber);
                    if (data.country && !!data.country.trim()) {
                        eeFormApiRef.current.setValue('employeeInfo_country', data.country); //TODO: this keeps coming back as null and does not prefill
                    }
                    if (data.state && !!data.state.trim()) {
                        eeFormApiRef.current.setValue('employeeInfo_state', data.state);
                    }
                    eeFormApiRef.current.setValue('employeeInfo_city', formatTitleCase(data.city));
                    eeFormApiRef.current.setValue('employeeInfo_street1', formatTitleCase(data.streetAddress1));
                    eeFormApiRef.current.setValue('employeeInfo_street2', formatTitleCase(data.streetAddress2));
                    eeFormApiRef.current.setValue('employeeInfo_postalcode', data.postalcode);
                    eeFormApiRef.current.setValue('employeeInfo_dob', data.dob);
                }
                setPrefillMessage('Employee found and information pre-filled successfully.');
                setShowSpinner(false);
            }
        })
            .catch((error) => {
            setIsPrefillError(true);
            setPrefillMessage('Failed to find information for employee. Retry search or enter employee information.');
            setShowSpinner(false);
        });
    };
    const SEARCH_CRITERIA_OPTIONS = [
        { label: labelEmployeeID, value: EMP_ID },
        { label: 'SSN', value: SSN },
    ];
    const uniqueIdentifier = lciApplication.uniqueIdIndicator.toUpperCase();
    const [searchCriteria, setSearchCriteria] = useState(EMP_ID);
    const showSSN = uniqueIdentifier === SSN || (uniqueIdentifier === BOTH && searchCriteria === SSN);
    const showEmpId = uniqueIdentifier === EMP_ID || (uniqueIdentifier === BOTH && searchCriteria === EMP_ID);
    const ssnField = showSSN ? (<TextInput field={'ssnPrefill'} label={'SSN'} validate={(value) => validateSSNFormat(value, LciValidationLabels.ssn)} mask={numbersOnly} maxLength={9} validateOnBlur={true} type={'textmask'} data-private={true} required={true}/>) : null;
    const employeeIdField = showEmpId ? (<TextInput field={'employeeIdPrefill'} label={labelEmployeeID} mask={validateAlphaNumeric} maxLength={35} validateOnBlur={true} data-private={true} type={'text'} required={true}/>) : null;
    const dateOfBirth = (field) => {
        return <NewDateInput field={field} label={'Date of birth'} required={true} data-private={true} validate={(props) => validateDateIsOnOrBeforeToday(props, t, LciValidationLabels.dateOfBirth)} minDate={parse('01/01/1900', 'P', new Date())} maxDate={new Date()}/>;
    };
    const clearPrefillAlert = () => {
        setIsPrefillError(false);
        setPrefillMessage('');
    };
    const prefillAlert = prefillMessage ? (<Alert type={isPrefillError ? "warning" : "success"} closeable={true} onClose={clearPrefillAlert}>
        {prefillMessage}
      </Alert>) : null;
    return (<>
      <Loader viewportCentered={true} id='ee-info-spinner' isShown={showSpinner}>
      <Heading elemType='h2' color='primary'>
        {labelEmployee} info
      </Heading>
      {<>
          <AccessibleForm getApi={setPrefillFormApi} onSubmit={handlePrefill} data-testid="prefillForm">
            <Row>
              <Col>
                <BorderLessGrayBox>
                  <Heading elemType='h3' elemStyle='h4'>
                    {labelEmployee} lookup
                  </Heading>
                </BorderLessGrayBox>
              </Col>
            </Row>
            {prefillAlert}
            <Row>
              <Col>
                Enter the {formatLowerCase(labelEmployee)} details to find the information to pre-fill and review this
                form.
              </Col>
            </Row>
            <Row className={'mt-4'}>
              {uniqueIdentifier === BOTH && (<Col lg={3} md={3} sm={12}>
                  <SelectInput field={'searchCriteria'} label={'Select search criteria'} options={SEARCH_CRITERIA_OPTIONS} initialValue={SEARCH_CRITERIA_DEFAULT_VALUE} onValueChange={(value) => {
                    clearPrefillAlert();
                    setSearchCriteria(value);
                }} validateOnBlur={true} type={'text'}/>
                </Col>)}
              <Col lg={3} md={3} sm={12}>
                {ssnField}
                {employeeIdField}
              </Col>
              <Col lg={3} md={3} sm={12}>
                <TextInput field={'lastNamePrefill'} label={'Last name'} mask={formatName} maxLength={MAX_LENGTH_LNAME} validate={(value) => validateName(value ? value.trim() : value, MAX_LENGTH_LNAME, LciValidationLabels.lastName, true)} validateOnBlur={true} type={'text'} data-private={true} required={true}/>
              </Col>
              <Col lg={3} md={3} sm={12}>
                {dateOfBirth('dobPrefill')}
              </Col>
            </Row>
            <Row className='mt-0 mb-3'>
              <Col lg={4} md={4} sm={12}>
                <Button buttonType='primary' style={{ width: '100%' }}>
                  Find employee information
                </Button>
              </Col>
            </Row>
          </AccessibleForm>
        </>}
      <AccessibleForm getApi={setFormApi} onSubmit={handleContinue} data-testid="infoForm">
        <Row>
          <Col>
            <BorderLessGrayBox>
              <Heading elemType='h3' elemStyle='h4'>
                {labelEmployee} Personal information
              </Heading>
            </BorderLessGrayBox>
          </Col>
        </Row>
        <Row className={'mt-3'}>
          <Col lg={5} md={5} sm={12}>
            <TextInput field={'employeeInfo_firstName'} label={'First name'} maxLength={MAX_LENGTH_FNAME} validate={(value) => validateName(value, MAX_LENGTH_FNAME, LciValidationLabels.firstName, true)} validateOnBlur={true} type={'text'} data-private={true} required={true}/>
          </Col>
          <Col lg={5} md={5} sm={12}>
            <TextInput field={'employeeInfo_lastName'} label={'Last name'} maxLength={MAX_LENGTH_LNAME} validate={(value) => validateName(value, MAX_LENGTH_LNAME, LciValidationLabels.lastName, true)} validateOnBlur={true} type={'text'} data-private={true} required={true}/>
          </Col>
          <Col lg={2} md={2} sm={12}>
            <TextInput ariaLabelText={'employeeInfo_middleInitial'} field={'employeeInfo_middleInitial'} label={'MI (optional)'} maxLength={1} mask={lettersOnly} validateOnBlur={true} type={'text'}/>
          </Col>
        </Row>
        <Row className='mt-0'>
          <Col xl={4} md={6} sm={12}>
            {dateOfBirth('employeeInfo_dob')}
          </Col>
          <Col xl={4} md={6} sm={12}>
            <SelectInput field={'employeeInfo_gender'} label={'Sex assigned at birth'} placeholderText={'Select a sex assigned at birth'} data-private={true} options={LCI_GENDER} validateOnBlur={true} type={'text'} required={true}/>
          </Col>
          <Col xl={4} md={6} sm={12}>
            <TextInput ariaLabelText={'employeeInfo_phoneNumber'} field={'employeeInfo_phoneNumber'} label={'Phone number (optional)'} data-private={true} mask={formatPhone} validate={(value) => validatePhoneNotRequired(value, LciValidationLabels.phoneNumber)} validateOnBlur={true} type={'text'}/>
          </Col>
          {claimant === 'employee' && event === 'accelerated death' && (<Col xl={4} md={6} sm={12}>
              <SelectInput ariaLabelText={'employeeInfo_maritalStatus'} field={'employeeInfo_maritalStatus'} label={'Marital status (optional)'} placeholderText={'Select a martial status (optional)'} options={LCI_EMPLOYEE_MARITAL_STATUS} type={'text'}/>
            </Col>)}
        </Row>
        <Address title={`${labelEmployee} address`} field={'employeeInfo'} required={true}/>
      </AccessibleForm>
      <StyledDiv>
        <Button aria-label={'Back'} buttonType={'secondary'} role='button' id='employeeInfo_back' type='button' onClick={handleBack}>
          Back
        </Button>
        <CancelLci step={'step 3'} pageTitle={`${labelEmployee} info`} sectionInfo={'Employee info'}/>
        <Button className='ml-0' aria-label={'Continue'} buttonType='primary' role='button' id='employeeInfo_continue' type='button' onClick={handleSubmit}>
          Continue
        </Button>
      </StyledDiv>
      </Loader>
    </>);
};
export default EmployeeInfo;
